/**
 * One line of a revaluation: money against a product, and no quantity anywhere.
 *
 * There is deliberately no `qty` field on this class. The backend sets the movement's quantity to
 * zero by construction rather than by exemption, so the caller is given no way to state one; adding
 * a field here would be the first step towards handing up a quantity that the ledger would then have
 * to decide what to do with. Nothing on the screen offers one either, because an operator who typed
 * a quantity would reasonably expect the stock level to move, and it will not.
 *
 * `warehouseLocationId` is required and recorded because an operator writing stock down is stating
 * which stock they were looking at. It is audit only: the weighted average is taken per product, so
 * the location on the row does not scope the money to that shelf.
 */
export class StockRevaluationLine {
  productId: number = -1;
  warehouseLocationId: number = -1;

  /**
   * What the product's whole holding is worth after the correction: a total across every unit of it,
   * in every location, rather than a rate per unit or an amount to move the value by.
   *
   * This replaced a signed `valueAdjustment` and the two mean opposite things - -500 was "take 500
   * off", 500 is "it is now worth 500". The backend refuses a body still carrying the old field
   * outright rather than dropping it, so a caller that was not updated is answered 400 instead of
   * being told a write-down succeeded when nothing was corrected.
   *
   * NULL, not 0, and that is the whole reason this default is what it is. Zero is now a legitimate
   * value - a holding written off entirely is worth nothing, and stating that is the correction an
   * operator most often needs - so "nobody typed anything" can no longer be represented by 0 and
   * has to stay distinguishable from it. Defaulting to 0 would let an operator who never touched
   * the field post a line writing the holding off to nothing. The backend refuses undefined, null
   * and '' for the same reason.
   */
  targetValue: number = null;
}

/**
 * Posted as one request. The backend runs the whole batch in a single transaction, so a multi-line
 * write-down cannot half-post and leave a product carrying part of a correction.
 *
 * One line per product, enforced by the backend and by `addLine` on the screen. Two lines naming the
 * same product state two different values for one holding and the later one would simply win, which
 * is not a reading worth guessing at. Two locations of the same product is exactly this case rather
 * than an exception to it: the value is product-wide, so naming two shelves is naming one holding
 * twice.
 */
export class StockRevaluation {
  referenceId: number = -1;
  lines: StockRevaluationLine[] = [];
}
